import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import {
  feedbackCategoryLabel, feedbackPriorityLabel, feedbackSubmitToLabel,
} from '../../data/feedbackConstants';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import { ArrowLeft, Download } from 'lucide-react';
import { downloadFeedbackAttachment, getApiErrorMessage } from '../../utils/feedbackDownload';

export default function MyFeedbackDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleDownload = async (attachmentId, name) => {
    try {
      await downloadFeedbackAttachment(attachmentId, name);
      toast({ title: 'Download started', description: name || 'Attachment saved.' });
    } catch (e) {
      toast({ title: 'Download failed', description: await getApiErrorMessage(e), variant: 'destructive' });
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/feedback/my/${id}`)
      .then((res) => setData(res.data?.data || null))
      .catch((e) => {
        toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id, toast]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild><Link to="/my-feedback"><ArrowLeft className="h-4 w-4 mr-2" />Back</Link></Button>
        <p className="text-muted-foreground">Feedback not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-feedback-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/my-feedback"><ArrowLeft className="h-4 w-4 mr-1" />My Feedback</Link>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-sm font-mono text-muted-foreground">{data.feedback_code}</p>
          <h1 className="text-2xl font-bold text-foreground mt-1">{data.title}</h1>
        </div>
        <FeedbackStatusBadge status={data.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-foreground">{data.description}</p>
            </CardContent>
          </Card>

          {(data.attachments || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle>Attachments</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.attachments.map((att) => (
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => handleDownload(att.id, att.original_name)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                  >
                    <Download className="h-4 w-4" />
                    {att.original_name}
                    <span className="text-muted-foreground">({att.file_type})</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Admin Replies</CardTitle></CardHeader>
            <CardContent>
              {(data.replies || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No replies yet.</p>
              ) : (
                <ul className="space-y-4">
                  {data.replies.map((r) => (
                    <li key={r.id} className="rounded-lg border border-border p-4 bg-muted/30">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium">{r.admin_name}</span>
                        <span className="text-muted-foreground">{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm">{r.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div><span className="text-muted-foreground">Category</span><p>{feedbackCategoryLabel(data.category)}</p></div>
              <div><span className="text-muted-foreground">Submit to</span><p>{feedbackSubmitToLabel(data.submit_to)}</p></div>
              <div><span className="text-muted-foreground">Priority</span><p className="capitalize">{feedbackPriorityLabel(data.priority)}</p></div>
              <div><span className="text-muted-foreground">Submitted</span><p>{data.created_at?.slice(0, 16).replace('T', ' ')}</p></div>
              <div><span className="text-muted-foreground">Browser</span><p>{data.browser || '—'}</p></div>
              <div><span className="text-muted-foreground">Device</span><p>{data.device || '—'}</p></div>
              {data.page_url && (
                <div><span className="text-muted-foreground">Page</span><p className="break-all text-xs">{data.page_url}</p></div>
              )}
            </CardContent>
          </Card>

          {(data.activity || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle>Activity</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {data.activity.map((a) => (
                    <li key={a.id} className="border-b border-border/50 pb-2 last:border-0">
                      <span className="font-medium capitalize">{a.action?.replace(/_/g, ' ')}</span>
                      {a.new_value && <span className="text-muted-foreground"> → {a.new_value.replace(/_/g, ' ')}</span>}
                      <p className="text-xs text-muted-foreground">{a.created_at?.slice(0, 16).replace('T', ' ')}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
